if (!customElements.get('buy-it-again')) {
  class BuyItAgain extends HTMLElement {
    constructor() {
      super();

      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.rebuySource = String(this.dataset.rebuySource || '').trim();
      this.dataSourceId = String(this.dataset.dataSourceId || '').trim();
      this.rebuyApiKey = String(this.dataset.rebuyApiKey || '').trim();
      this.customerId = String(this.dataset.customerId || '').trim();
      this.productsToShow = Math.min(Math.max(Number(this.dataset.productsToShow) || 10, 1), 25);
      this.fallback = this.closest('[data-featured-rebuy-section]')?.querySelector('[data-collection-fallback]');

      this.productList = this.querySelector('[data-product-list]');
      this.sliderComponent = this.querySelector('slider-component') || this.closest('slider-component');
      this.sliderControls = this.querySelector('[data-slider-controls]');
      this.placeholderTemplate = this.querySelector('[data-product-placeholder-template]');
      this.handoffDeadline = performance.now() + 5000;

      const customDataSourceReady =
        this.rebuySource === 'custom_data_source' && Boolean(this.dataSourceId);
      const purchasedProductsReady =
        this.rebuySource === 'purchased_products' && Boolean(this.customerId);

      if (
        !this.rebuyApiKey ||
        !this.fallback ||
        !this.productList ||
        (!customDataSourceReady && !purchasedProductsReady)
      ) {
        this.restoreFallback();
        return;
      }

      window.addEventListener('resize', this.handleResize);

      this.showLoadingState();
      this.loadingFallbackTimer = window.setTimeout(() => this.restoreFallback(), 5000);
      this.loadProducts();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
      window.clearTimeout(this.loadingFallbackTimer);
      this.requestController?.abort();

      if (this.fallback) {
        this.fallback.hidden = false;
      }
    }

    async loadProducts() {
      this.requestController = new AbortController();

      const remainingTime = Math.max(this.handoffDeadline - performance.now(), 0);
      const requestTimeout = window.setTimeout(() => this.requestController.abort(), remainingTime);

      try {
        const products =
          this.rebuySource === 'purchased_products'
            ? await this.fetchPurchasedProducts(this.requestController.signal)
            : await this.fetchDataSourceProducts(this.requestController.signal);
        const handles = this.getUniqueHandles(products);

        if (!handles.length) return;

        const cards = await Promise.all(
          handles.map((handle) => this.fetchProductCard(handle, this.requestController.signal))
        );
        const validCards = cards.filter(Boolean);

        if (!validCards.length || performance.now() >= this.handoffDeadline) return;
        if (this.fallback.contains(document.activeElement)) return;

        const cardItems = validCards.map((card, index) => this.buildProductCard(card, index));
        this.productList.replaceChildren(...cardItems);

        this.hidden = false;
        this.fallback.hidden = true;
        this.personalisedContentVisible = true;
        this.dataset.buyItAgainReady = 'true';
        delete this.dataset.buyItAgainLoading;
        this.removeAttribute('aria-busy');
        window.clearTimeout(this.loadingFallbackTimer);

        requestAnimationFrame(() => {
          this.sliderComponent?.resetPages();
          this.updateSliderControls();
        });
      } finally {
        window.clearTimeout(requestTimeout);
      }
    }

    async fetchDataSourceProducts(signal) {
      try {
        const params = new URLSearchParams({
          key: this.rebuyApiKey,
        });
        if (this.customerId) params.set('shopify_customer_id', this.customerId);
        const response = await fetch(
          `https://rebuyengine.com/api/v1/custom/id/${encodeURIComponent(this.dataSourceId)}?${params.toString()}`,
          {
            credentials: 'omit',
            headers: { Accept: 'application/json' },
            signal,
          }
        );

        if (!response.ok) return [];

        const payload = await response.json();
        return Array.isArray(payload?.data) ? payload.data : [];
      } catch (error) {
        return [];
      }
    }

    async fetchPurchasedProducts(signal) {
      if (!this.customerId) return [];

      try {
        const params = new URLSearchParams({
          key: this.rebuyApiKey,
          shopify_customer_id: this.customerId,
        });
        const response = await fetch(
          `https://rebuyengine.com/api/v1/products/purchased?${params.toString()}`,
          {
            credentials: 'omit',
            headers: { Accept: 'application/json' },
            signal,
          }
        );

        if (!response.ok) return [];

        const payload = await response.json();
        return Array.isArray(payload?.data) ? payload.data : [];
      } catch (error) {
        return [];
      }
    }

    showLoadingState() {
      if (!this.placeholderTemplate) return;

      if (!this.productList.children.length) {
        const placeholders = Array.from(
          { length: Math.min(this.productsToShow, 4) },
          () => this.placeholderTemplate.content.firstElementChild.cloneNode(true)
        );

        this.productList.replaceChildren(...placeholders);
      }
      this.hidden = false;
      this.fallback.hidden = true;
      this.dataset.buyItAgainLoading = 'true';
      this.setAttribute('aria-busy', 'true');
    }

    restoreFallback() {
      if (this.personalisedContentVisible) return;

      this.hidden = true;
      this.fallback.hidden = false;
      this.productList.replaceChildren();
      delete this.dataset.buyItAgainLoading;
      this.removeAttribute('aria-busy');
    }

    getUniqueHandles(products) {
      const handles = [];
      const seenHandles = new Set();

      for (const product of products) {
        const handle = String(product?.handle || product?.product?.handle || '').trim();
        const normalizedHandle = handle.toLowerCase();

        if (!handle || seenHandles.has(normalizedHandle)) continue;

        seenHandles.add(normalizedHandle);
        handles.push(handle);

        if (handles.length >= this.productsToShow) break;
      }

      return handles;
    }

    async fetchProductCard(handle, signal) {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(
          `${root}products/${encodeURIComponent(handle)}?view=buy-it-again-card`,
          {
            headers: { Accept: 'text/html' },
            signal,
          }
        );

        if (!response.ok) return '';

        const card = (await response.text()).trim();
        return card || '';
      } catch (error) {
        return '';
      }
    }

    buildProductCard(card, cardIndex) {
      const item = document.createElement('li');
      item.id = `Slide-${this.id}-${cardIndex + 1}`;
      item.className = 'grid__item slider__slide';
      item.innerHTML = card;

      item.querySelectorAll('.quick-add').forEach((quickAdd, quickAddIndex) => {
        const idMap = new Map();

        quickAdd.querySelectorAll('[id]').forEach((element, elementIndex) => {
          const currentId = element.getAttribute('id');
          if (!currentId) return;

          const uniqueId = `${currentId}-${this.id}-${cardIndex + 1}-${quickAddIndex + 1}-${elementIndex + 1}`;
          idMap.set(currentId, uniqueId);
          element.id = uniqueId;
        });

        quickAdd
          .querySelectorAll('[for], [form], [aria-labelledby], [aria-describedby], [aria-controls]')
          .forEach((element) => {
            ['for', 'form', 'aria-labelledby', 'aria-describedby', 'aria-controls'].forEach((attribute) => {
              const value = element.getAttribute(attribute);
              if (!value) return;

              element.setAttribute(
                attribute,
                value
                  .split(/\s+/)
                  .map((reference) => idMap.get(reference) || reference)
                  .join(' ')
              );
            });
          });
      });

      const cardIds = new Set(
        Array.from(item.querySelectorAll('[id]'), (element) => element.getAttribute('id')).filter(Boolean)
      );

      item.querySelectorAll('[aria-labelledby]').forEach((element) => {
        const validReferences = element
          .getAttribute('aria-labelledby')
          .split(/\s+/)
          .filter((reference) => cardIds.has(reference) || document.getElementById(reference));

        if (validReferences.length) {
          element.setAttribute('aria-labelledby', validReferences.join(' '));
        } else {
          element.removeAttribute('aria-labelledby');
        }
      });

      return item;
    }

    handleResize() {
      if (!this.personalisedContentVisible) return;
      this.updateSliderControls();
    }

    updateSliderControls() {
      if (!this.sliderControls || !this.productList) return;

      this.sliderControls.hidden = this.productList.scrollWidth <= this.productList.clientWidth + 1;
    }
  }

  customElements.define('buy-it-again', BuyItAgain);
}
